import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { analyzeReflection } from '../utils/analysis';

export default function Analytics({ logs = [] }) {
  // Helper to convert mood to numerical score for the chart
  const getMoodScore = (mood) => {
    switch (mood) {
      case 'great': return 5;
      case 'good': return 4;
      case 'okay': return 3;
      case 'stressed': return 2;
      case 'overwhelmed': return 1;
      default: return 3;
    }
  };

  // --- 1. Mood Trend LineChart ---
  const moodTrendData = useMemo(() => {
    if (!logs || logs.length === 0) return [];
    // Reverse to chronological order for the chart (assuming logs are newest-first)
    return [...logs].reverse().slice(-14).map((log, index) => ({
      index: index + 1,
      date: new Date(log.date || log.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' }),
      score: getMoodScore(log.mood),
      label: log.mood
    }));
  }, [logs]);

  // --- 2. Keyword Cloud (Tag Cloud) ---
  const keywordData = useMemo(() => {
    if (!logs || logs.length === 0) return [];
    // Get last 10 logs
    const recentLogs = logs.slice(0, 10);
    const frequencies = {};
    
    recentLogs.forEach(log => {
      // either use existing array or calculate it via analyzeReflection safely
      const keywords = Array.isArray(log.keywords) 
        ? log.keywords 
        : analyzeReflection(log.thoughts || '');
        
      keywords.forEach(word => {
        if (word) {
          frequencies[word] = (frequencies[word] || 0) + 1;
        }
      });
    });

    return Object.entries(frequencies)
      .map(([word, count]) => ({ word, count }))
      .sort((a, b) => b.count - a.count);
  }, [logs]);

  // Function to determine font size based on relative frequency
  const maxKeywordCount = keywordData.length > 0 ? Math.max(...keywordData.map(k => k.count), 1) : 1;
  const getKeywordStyle = (count) => {
    const ratio = count / maxKeywordCount;
    if (ratio > 0.8) return "text-3xl font-bold text-emerald-800";
    if (ratio > 0.5) return "text-xl font-semibold text-emerald-700";
    if (ratio > 0.2) return "text-lg font-medium text-emerald-600/80";
    return "text-sm text-emerald-600/60";
  };

  // --- 3. Consistency Heatmap (GitHub Style) ---
  const heatmapData = useMemo(() => {
    if (!logs || logs.length === 0) return [];
    // Generate the last 28 days
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Create a Set of all log timestamps (normalized to start of day)
    const logTimestamps = new Set(logs.map(log => {
      const d = new Date(log.date || log.timestamp || log.createdAt);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    }));

    for (let i = 27; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const isCheckedIn = logTimestamps.has(d.getTime());
      
      days.push({
        date: d.toLocaleDateString(),
        active: isCheckedIn
      });
    }
    return days;
  }, [logs]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/90 backdrop-blur px-3 py-2 border border-stone-200 shadow-sm rounded-lg">
          <p className="text-xs text-stone-500 mb-1">{payload[0].payload.date}</p>
          <p className="text-sm font-medium text-emerald-700 capitalize flex items-center gap-2">
            Mood: {payload[0].payload.label}
          </p>
        </div>
      );
    }
    return null;
  };

  // Moved early return after hooks
  if (!logs || logs.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-12 mt-6 text-center border border-stone-100 shadow-sm">
        <h3 className="text-xl font-light text-stone-800 mb-2">Reflect to Discover</h3>
        <p className="text-stone-500">Your analytics will appear here after your first check-in.</p>
      </div>
    );
  }


  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Container for minimalist Sage styling */}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Mood Trend Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-8 border border-stone-100 shadow-sm transition-all hover:shadow-md">
          <h3 className="text-lg font-light tracking-wide text-stone-800 mb-6 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            Mood Trend
          </h3>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={moodTrendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E7E5E4" />
                <XAxis 
                  dataKey="date" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 11, fill: '#A8A29E' }} 
                  dy={10} 
                />
                <YAxis 
                  domain={[1, 5]} 
                  ticks={[1, 2, 3, 4, 5]} 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 11, fill: '#A8A29E' }} 
                  tickFormatter={(val) => {
                    return { 1: 'Low', 3: 'Okay', 5: 'Great' }[val] || '';
                  }}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#D6D3D1', strokeWidth: 1, strokeDasharray: '4 4' }} />
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke="#10B981" 
                  strokeWidth={3} 
                  dot={{ r: 4, fill: '#10B981', strokeWidth: 2, stroke: '#FFFFFF' }} 
                  activeDot={{ r: 6, fill: '#059669', stroke: '#FFF' }}
                  animationDuration={1500}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Consistency Heatmap */}
        <div className="bg-white rounded-2xl p-8 border border-stone-100 shadow-sm transition-all hover:shadow-md flex flex-col">
          <h3 className="text-lg font-light tracking-wide text-stone-800 mb-6 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            Consistency
          </h3>
          <div className="flex-1 flex flex-col justify-center items-center">
            {/* 4 rows by 7 columns grid roughly equaling a month */}
            <div className="grid grid-cols-7 gap-2 max-w-[200px] w-full mx-auto">
              {heatmapData.map((day, i) => (
                <div 
                  key={i} 
                  title={day.date}
                  className={`aspect-square w-full rounded-sm transition-all duration-500 hover:scale-110 ${
                    day.active 
                      ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.4)]' 
                      : 'bg-stone-100'
                  }`}
                />
              ))}
            </div>
            <div className="flex justify-between items-center w-full max-w-[200px] mt-4 text-[10px] text-stone-400 font-medium uppercase tracking-wider">
              <span>Less</span>
              <div className="flex gap-1">
                <div className="w-2.5 h-2.5 rounded-xs bg-stone-100"></div>
                <div className="w-2.5 h-2.5 rounded-xs bg-emerald-200"></div>
                <div className="w-2.5 h-2.5 rounded-xs bg-emerald-400"></div>
              </div>
              <span>More</span>
            </div>
          </div>
        </div>

        {/* Keyword Cloud */}
        <div className="lg:col-span-3 bg-white rounded-2xl p-8 border border-stone-100 shadow-sm transition-all hover:shadow-md">
          <h3 className="text-lg font-light tracking-wide text-stone-800 mb-6 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            Mind Garden
          </h3>
          
          {keywordData.length > 0 ? (
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 py-8 px-4 rounded-xl bg-gradient-to-br from-stone-50 to-white border border-stone-100/50">
              {keywordData.map((item, idx) => (
                <span 
                  key={idx} 
                  className={`transition-all duration-500 hover:scale-110 cursor-default ${getKeywordStyle(item.count)}`}
                  style={{
                    // Add slight random rotation to make it look organic
                    transform: `rotate(${Math.sin(idx * 456) * 4}deg) translateY(${Math.cos(idx * 789) * 4}px)`,
                  }}
                  title={`Appeared ${item.count} times`}
                >
                  {item.word}
                </span>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <p className="text-stone-400 font-light">Not enough thoughts shared yet to cultivate your garden.</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
